import { useCallback, useState } from 'react';
import { Color, Size, t, pageKindLineText, postalAddressLine, suiteOnLocationQueueLine } from '@bms2/shared';
import { Button } from '../../components/controls';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { AsyncStatePanel } from '../../components/composites/AsyncStatePanel';
import { Tile, type TileItem } from '../../components/composites/Tile';
import { PageModule } from '../../shell/PageModule';
import { PAGES_PATH, post, useRows } from './api';

/**
 * 🔒 The Address Needs Review queue at `/locations/review` (piece 13, decision
 * 0232). Each `LocationRecord` whose verification is NeedsReview, and each pair
 * the normalised-address match flagged, with the Sites beneath each row counted.
 * 🔨 Resolve keeps one building, re-points every Site beneath the other at it,
 * and soft-deletes the loser; it writes no `RecordMerge` row, because a Location
 * keeps no printed id that must redirect later.
 */
export const ADDRESS_REVIEW_ROUTE = '/locations/review';

export interface ReviewRow {
  id: string;
  addressLines: string[];
  locality: string | null;
  administrativeArea: string | null;
  postalCode: string | null;
  verificationStatus: string;
  siteCount: number;
  duplicateOf: string[];
  /** The suite part still in the address lines, which no single site could take; absent on an older answer. */
  suitePart?: string | null;
}

export const REVIEW_TILE_MIN_WIDTH = 280;
export const REVIEW_TILE_MAX_WIDTH = 720;

function itemsFor(row: ReviewRow): TileItem[] {
  return [
    {
      type: 'labelValueGrid',
      pairs: [
        { label: t('review.city'), value: postalAddressLine({ locality: row.locality, administrativeArea: row.administrativeArea, postalCode: row.postalCode }) ?? '—' },
        { label: t('review.verification'), value: row.verificationStatus },
        { label: t('review.sites'), value: String(row.siteCount) },
        { label: t('review.duplicates'), value: String(row.duplicateOf.length) },
        // 🔒 The suite the building's lines still carry, named so a person sets it on the right site (decision 7400).
        ...(row.suitePart ? [{ label: t('review.suitePart'), value: suiteOnLocationQueueLine(row.suitePart) }] : []),
      ],
    },
  ];
}

export function AddressReviewPage({ apiBaseUrl = '' }: { apiBaseUrl?: string }) {
  const { state, reload } = useRows<ReviewRow>(`${PAGES_PATH}/locations/review`, apiBaseUrl);
  const [error, setError] = useState<string | null>(null);

  const resolve = useCallback(
    async (keepLocationId: string, dropLocationId: string) => {
      setError(null);
      const answer = await post(`${PAGES_PATH}/locations/review/resolve`, { keepLocationId, dropLocationId }, apiBaseUrl);
      if (!answer.ok) {
        setError(answer.message);
        return;
      }
      reload();
    },
    [apiBaseUrl, reload],
  );

  return (
    <PageModule title={t('review.title')} subtitle={pageKindLineText({ page: 'named', route: '/locations/review' })}>
      <div data-page="address-review">
        {error && <AlertBanner tone="error" message={error} action={{ label: t('modal.dismiss'), onAction: () => setError(null) }} />}
        <AsyncStatePanel state={state} emptyAction={t('review.empty')} onRetry={reload}>
          {(rows) => (
            <ul data-review-rows={rows.length}>
              {rows.map((row) => (
                <li key={row.id} data-review-row={row.id}>
                  <Tile title={row.addressLines[0] ?? row.id} items={itemsFor(row)} minWidth={REVIEW_TILE_MIN_WIDTH} maxWidth={REVIEW_TILE_MAX_WIDTH} />
                  {/* Resolve keeps the row it is offered against and retires this one. */}
                  {row.duplicateOf.map((otherId) => (
                    <Button
                      key={otherId}
                      size={Size.Small}
                      color={Color.Navy}
                      text={[t('review.resolve')]}
                      actions={[() => void resolve(otherId, row.id)]}
                    />
                  ))}
                </li>
              ))}
            </ul>
          )}
        </AsyncStatePanel>
      </div>
    </PageModule>
  );
}
